// src/pages/CouponIssuePage.tsx
import * as React from "react";
import useSWR from "swr";
import {
  Card,
  Tabs,
  Typography,
  Button,
  Tag,
  Space,
  Select,
  Input,
  InputNumber,
  Switch,
  Divider,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  bulkIssueCouponApi,
  filterIssueCouponApi,
  filterIssueCouponPreviewApi,
  type FilterIssueCouponPayload,
} from "src/api/couponIssuance/couponIssuanceApi";
import Swal from "sweetalert2";
import { getPetBreedListApi } from "src/api/petBreed/petBreedApi";
import { snackbarStore } from "src/store/snackbarStore";
import CustomButton from "src/components/common/CustomButton";
import CustomerSelectModal, {
  type SelectedCustomer,
} from "src/components/coupon/CustomerSelectModal";
import CouponSelectModal, { type SelectedCoupon } from "src/components/coupon/CouponSelectModal";
import ProductSelectModal, { type SelectedProduct } from "src/components/coupon/ProductSelectModal";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

// 탭1: 다중 사용자 + 다중 쿠폰 발급
function BulkIssueTab() {
  const [selectedCustomers, setSelectedCustomers] = React.useState<SelectedCustomer[]>([]);
  const [selectedCoupons, setSelectedCoupons] = React.useState<SelectedCoupon[]>([]);
  const [customerModalOpen, setCustomerModalOpen] = React.useState(false);
  const [couponModalOpen, setCouponModalOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleReset = () => {
    setSelectedCustomers([]);
    setSelectedCoupons([]);
  };

  const handleSubmit = async () => {
    if (selectedCustomers.length === 0) {
      toast("고객을 선택해주세요.", "warning");
      return;
    }
    if (selectedCoupons.length === 0) {
      toast("쿠폰을 선택해주세요.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const res = await bulkIssueCouponApi({
        userIds: selectedCustomers.map((c) => c.id),
        couponIds: selectedCoupons.map((c) => c.couponId),
      });

      if (res.statusCode === 1) {
        const data = res.data;
        toast(
          `발급 완료: 성공 ${(data?.successCount ?? 0).toLocaleString()}건, 스킵 ${(
            data?.skippedCount ?? 0
          ).toLocaleString()}건`,
          "success"
        );
        handleReset();
      } else {
        toast(res.message || "발급 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "발급 중 오류 발생", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Typography.Title level={5}>고객 선택</Typography.Title>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        쿠폰을 발급할 고객을 선택하세요.
      </Typography.Text>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setCustomerModalOpen(true)}>
          고객 선택
        </Button>
        {selectedCustomers.map((customer) => (
          <Tag
            key={customer.id}
            closable
            onClose={() => setSelectedCustomers((prev) => prev.filter((c) => c.id !== customer.id))}
          >
            {customer.username} ({customer.email})
          </Tag>
        ))}
      </div>

      <Typography.Title level={5}>쿠폰 선택</Typography.Title>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        발급할 쿠폰을 선택하세요. (복수 선택 가능)
      </Typography.Text>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setCouponModalOpen(true)}>
          쿠폰 선택
        </Button>
        {selectedCoupons.map((coupon) => (
          <Tag
            key={coupon.couponId}
            closable
            onClose={() =>
              setSelectedCoupons((prev) => prev.filter((c) => c.couponId !== coupon.couponId))
            }
          >
            {coupon.couponName} [{coupon.couponCode}]
          </Tag>
        ))}
      </div>

      <Divider />

      <Space>
        <CustomButton variant="outlined" onClick={handleReset}>
          초기화
        </CustomButton>
        <CustomButton variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "발급 중..." : "발급하기"}
        </CustomButton>
      </Space>

      <CustomerSelectModal
        open={customerModalOpen}
        selectedIds={selectedCustomers.map((c) => c.id)}
        selectedItems={selectedCustomers}
        onClose={() => setCustomerModalOpen(false)}
        onConfirm={setSelectedCustomers}
      />

      <CouponSelectModal
        open={couponModalOpen}
        selectedIds={selectedCoupons.map((c) => c.couponId)}
        selectedItems={selectedCoupons}
        onClose={() => setCouponModalOpen(false)}
        onConfirm={setSelectedCoupons}
      />
    </div>
  );
}

// 탭2: 필터 기반 쿠폰 발급
function FilterIssueTab() {
  const [selectedCoupons, setSelectedCoupons] = React.useState<SelectedCoupon[]>([]);
  const [expiredAt, setExpiredAt] = React.useState<string>("");
  const [couponModalOpen, setCouponModalOpen] = React.useState(false);
  const [productModalOpen, setProductModalOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // 필터 상태
  const [joinedFrom, setJoinedFrom] = React.useState<string>("");
  const [joinedTo, setJoinedTo] = React.useState<string>("");
  const [includeDormant, setIncludeDormant] = React.useState(false);
  const [lastLoginFrom, setLastLoginFrom] = React.useState<string>("");
  const [lastLoginTo, setLastLoginTo] = React.useState<string>("");
  const [gender, setGender] = React.useState<"MALE" | "FEMALE" | "">("");
  const [hasPet, setHasPet] = React.useState<boolean | null>(null);
  // 서버 필터는 소문자 dog/cat 를 받습니다.
  const [petType, setPetType] = React.useState<"dog" | "cat" | "">("");
  const [petBreedId, setPetBreedId] = React.useState<number | null>(null);
  const [petWeightMin, setPetWeightMin] = React.useState<number | null>(null);
  const [petWeightMax, setPetWeightMax] = React.useState<number | null>(null);
  const [purchasedProducts, setPurchasedProducts] = React.useState<SelectedProduct[]>([]);
  const [purchasedFrom, setPurchasedFrom] = React.useState<string>("");
  const [purchasedTo, setPurchasedTo] = React.useState<string>("");

  // 펫 품종 목록
  const { data: petBreedData } = useSWR(
    petType ? ["petBreed/list", petType] : null,
    async () => {
      const res = await getPetBreedListApi({
        // 품종 조회 API 는 대문자 DOG/CAT 를 사용합니다.
        type: petType.toUpperCase() as "DOG" | "CAT",
        useFlag: "Y",
        page: 0,
        size: 1000,
        order: "name,asc",
      });
      if (res.statusCode === 1) return res.data?.content ?? [];
      return [];
    },
    { revalidateOnFocus: false }
  );

  const petBreedList = petBreedData ?? [];

  const handleReset = () => {
    setSelectedCoupons([]);
    setExpiredAt("");
    setJoinedFrom("");
    setJoinedTo("");
    setIncludeDormant(false);
    setLastLoginFrom("");
    setLastLoginTo("");
    setGender("");
    setHasPet(null);
    setPetType("");
    setPetBreedId(null);
    setPetWeightMin(null);
    setPetWeightMax(null);
    setPurchasedProducts([]);
    setPurchasedFrom("");
    setPurchasedTo("");
  };

  const handleSubmit = async () => {
    if (selectedCoupons.length === 0) {
      toast("쿠폰을 선택해주세요.", "warning");
      return;
    }

    const payload: FilterIssueCouponPayload = {
      couponIds: selectedCoupons.map((c) => c.couponId),
      expiredAt: expiredAt || undefined,
      filter: {
        joinedFrom: joinedFrom || undefined,
        joinedTo: joinedTo || undefined,
        includeDormant,
        lastLoginFrom: lastLoginFrom || null,
        lastLoginTo: lastLoginTo || null,
        gender: gender || null,
        hasPet,
        petType: petType || null,
        petBreedId,
        petWeightMin,
        petWeightMax,
        purchasedProductIds:
          purchasedProducts.length > 0 ? purchasedProducts.map((p) => p.id) : undefined,
        purchasedFrom: purchasedFrom || null,
        purchasedTo: purchasedTo || null,
      },
    };

    setSubmitting(true);
    try {
      // 1. Preview API 호출
      const previewRes = await filterIssueCouponPreviewApi(payload);

      if (previewRes.statusCode !== 1) {
        toast(previewRes.message || "미리보기 실패", "error");
        setSubmitting(false);
        return;
      }

      const preview = previewRes.data;

      // 쿠폰별 발급 예정 내역
      const couponRows = (preview?.couponDetails ?? [])
        .map(
          (detail) => `
            <li style="margin-bottom: 6px;">
              <strong>${detail.couponName}</strong> [${detail.couponCode}]<br/>
              이미 발급 ${detail.alreadyIssuedCount.toLocaleString()}명 ·
              발급 예정 <span style="color: #1890ff; font-weight: bold;">${detail.actualIssueCount.toLocaleString()}명</span>
              ${detail.issuable ? "" : ' <span style="color: #cf1322;">(발급 불가 — 발급 한도 초과)</span>'}
            </li>`
        )
        .join("");

      // 2. Swal로 확인
      const result = await Swal.fire({
        title: "쿠폰 발급 확인",
        html: `
          <div style="text-align: left; line-height: 1.8;">
            <p><strong>대상 고객 수:</strong> ${preview?.totalTargetCount?.toLocaleString() ?? 0}명</p>
            <ul style="padding-left: 20px; margin: 8px 0 0;">${couponRows}</ul>
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "발급하기",
        cancelButtonText: "취소",
        confirmButtonColor: "#1890ff",
      });

      if (!result.isConfirmed) {
        setSubmitting(false);
        return;
      }

      // 3. 실제 발급 API 호출
      const res = await filterIssueCouponApi(payload);

      if (res.statusCode === 1) {
        const data = res.data;
        toast(
          `발급 완료: ${(data?.totalIssuedCount ?? 0).toLocaleString()}건 발급, ${(
            data?.totalSkippedCount ?? 0
          ).toLocaleString()}건 스킵`,
          "success"
        );
        handleReset();
      } else {
        toast(res.message || "발급 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "발급 중 오류 발생", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Typography.Title level={5}>쿠폰 선택</Typography.Title>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        발급할 쿠폰을 선택하세요. (복수 선택 가능)
      </Typography.Text>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setCouponModalOpen(true)}>
          쿠폰 선택
        </Button>
        {selectedCoupons.map((coupon) => (
          <Tag
            key={coupon.couponId}
            closable
            onClose={() =>
              setSelectedCoupons((prev) => prev.filter((c) => c.couponId !== coupon.couponId))
            }
          >
            {coupon.couponName} [{coupon.couponCode}]
          </Tag>
        ))}
      </div>

      <Typography.Title level={5}>만료일 설정 (선택)</Typography.Title>
      <Input
        type="datetime-local"
        value={expiredAt}
        onChange={(e) => setExpiredAt(e.target.value)}
        style={{ maxWidth: 300, marginBottom: 24 }}
        placeholder="미입력 시 쿠폰 기본 만료일 적용"
      />

      <Divider />

      <Typography.Title level={5}>발급 대상 필터</Typography.Title>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* 가입일 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            가입일
          </Typography.Text>
          <Space>
            <Input
              type="datetime-local"
              value={joinedFrom}
              onChange={(e) => setJoinedFrom(e.target.value)}
              style={{ width: 220 }}
            />
            <span>~</span>
            <Input
              type="datetime-local"
              value={joinedTo}
              onChange={(e) => setJoinedTo(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        </div>

        {/* 마지막 로그인 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            마지막 로그인
          </Typography.Text>
          <Space>
            <Input
              type="datetime-local"
              value={lastLoginFrom}
              onChange={(e) => setLastLoginFrom(e.target.value)}
              style={{ width: 220 }}
            />
            <span>~</span>
            <Input
              type="datetime-local"
              value={lastLoginTo}
              onChange={(e) => setLastLoginTo(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        </div>

        {/* 휴면 회원 포함 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            휴면 회원 포함
          </Typography.Text>
          <Switch checked={includeDormant} onChange={setIncludeDormant} />
        </div>

        {/* 성별 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            성별
          </Typography.Text>
          <Select
            value={gender}
            onChange={setGender}
            style={{ width: 200 }}
            allowClear
            placeholder="전체"
            options={[
              { value: "MALE", label: "남성" },
              { value: "FEMALE", label: "여성" },
            ]}
          />
        </div>

        {/* 반려동물 보유 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            반려동물 보유
          </Typography.Text>
          <Select
            value={hasPet === null ? "" : hasPet ? "true" : "false"}
            onChange={(val) => setHasPet(val === "" ? null : val === "true")}
            style={{ width: 200 }}
            allowClear
            placeholder="전체"
            options={[
              { value: "true", label: "있음" },
              { value: "false", label: "없음" },
            ]}
          />
        </div>

        {/* 펫 타입 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            펫 타입
          </Typography.Text>
          <Select
            value={petType}
            onChange={(val) => {
              setPetType(val);
              setPetBreedId(null);
            }}
            style={{ width: 200 }}
            allowClear
            placeholder="전체"
            options={[
              { value: "dog", label: "강아지" },
              { value: "cat", label: "고양이" },
            ]}
          />
        </div>

        {/* 펫 품종 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            펫 품종
          </Typography.Text>
          <Select
            value={petBreedId}
            onChange={setPetBreedId}
            style={{ width: 300 }}
            allowClear
            placeholder="전체"
            showSearch
            disabled={!petType}
            options={petBreedList.map((breed) => ({
              value: breed.id,
              label: breed.nameKr,
            }))}
          />
        </div>

        {/* 펫 몸무게 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            펫 몸무게 (kg)
          </Typography.Text>
          <Space>
            <InputNumber
              value={petWeightMin}
              onChange={(val) => setPetWeightMin(val)}
              placeholder="최소"
              min={0}
              style={{ width: 100 }}
            />
            <span>~</span>
            <InputNumber
              value={petWeightMax}
              onChange={(val) => setPetWeightMax(val)}
              placeholder="최대"
              min={0}
              style={{ width: 100 }}
            />
          </Space>
        </div>

        <Divider style={{ margin: "8px 0" }} />

        {/* 구매한 상품 */}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0, paddingTop: 4 }}>
            구매한 상품
          </Typography.Text>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => setProductModalOpen(true)}>
              상품 선택
            </Button>
            {purchasedProducts.map((product) => (
              <Tag
                key={product.id}
                closable
                onClose={() =>
                  setPurchasedProducts((prev) => prev.filter((p) => p.id !== product.id))
                }
              >
                {product.name}
              </Tag>
            ))}
          </div>
        </div>

        {/* 구매 기간 */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text strong style={{ width: 120, flexShrink: 0 }}>
            구매 기간
          </Typography.Text>
          <Space>
            <Input
              type="datetime-local"
              value={purchasedFrom}
              onChange={(e) => setPurchasedFrom(e.target.value)}
              style={{ width: 220 }}
            />
            <span>~</span>
            <Input
              type="datetime-local"
              value={purchasedTo}
              onChange={(e) => setPurchasedTo(e.target.value)}
              style={{ width: 220 }}
            />
          </Space>
        </div>
      </div>

      <Divider />

      <Space>
        <CustomButton variant="outlined" onClick={handleReset}>
          초기화
        </CustomButton>
        <CustomButton variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "발급 중..." : "발급하기"}
        </CustomButton>
      </Space>

      <CouponSelectModal
        open={couponModalOpen}
        selectedIds={selectedCoupons.map((c) => c.couponId)}
        selectedItems={selectedCoupons}
        onClose={() => setCouponModalOpen(false)}
        onConfirm={setSelectedCoupons}
      />

      <ProductSelectModal
        open={productModalOpen}
        selectedIds={purchasedProducts.map((p) => p.id)}
        selectedItems={purchasedProducts}
        onClose={() => setProductModalOpen(false)}
        onConfirm={setPurchasedProducts}
      />
    </div>
  );
}

export default function CouponIssuePage() {
  return (
    <div style={{ padding: 24, width: "100%" }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 24 }}>
          쿠폰 발급
        </Typography.Title>

        <Tabs
          items={[
            {
              key: "bulk",
              label: "다중 발급",
              children: <BulkIssueTab />,
            },
            {
              key: "filter",
              label: "조건 발급",
              children: <FilterIssueTab />,
            },
          ]}
        />
      </Card>
    </div>
  );
}
